import { FILTER_OPERATOR } from '@/app/community/constants/communityFilters';

// All recognized operator strings (OR, AND, OR_NOT, AND_NOT).
export type FilterOperator = (typeof FILTER_OPERATOR)[keyof typeof FILTER_OPERATOR];

const VALID_OPERATORS = new Set<string>(Object.values(FILTER_OPERATOR));

// ---------------------------------------------------------------------------
// Read side: URL/query string → operator state
// ---------------------------------------------------------------------------

/**
 * Split a `<op>:<values>` string into its operator and values segments.
 * - Returns `{ operator, values }` when a recognized operator prefix is present.
 * - Returns `{ operator: fallbackOperator, values: param }` otherwise, so call sites can
 *   treat un-prefixed input the same shape as prefixed input.
 *
 * Note: `values` is returned as the raw substring after the colon — splitting by `,` and
 * casting elements is the caller's responsibility because the element type varies per field
 * (numbers for tagIds, strings for messageTypes, booleans for isSpam, etc.).
 */
export function parseOperatorPrefix(
  param: string | undefined | null,
  fallbackOperator: FilterOperator | undefined = FILTER_OPERATOR.OR,
): { operator: FilterOperator | undefined; values: string | undefined | null } {
  if (typeof param === 'string' && param.includes(':')) {
    const [operator, values] = param.split(':');
    if (VALID_OPERATORS.has(operator)) {
      return { operator: operator as FilterOperator, values };
    }
  }
  return { operator: fallbackOperator, values: param };
}

/**
 * Like `parseOperatorPrefix` but tolerates boolean and nullish inputs — useful for query
 * fields that may arrive as `true | string | undefined` (e.g. `isSpam`, `isQuestion`).
 * Returns `{ operator, value }` (singular `value`) to mirror downstream usage where the
 * value is treated as a single primitive rather than a CSV.
 *
 * Unlike `parseOperatorPrefix`, this helper returns `operator: undefined` (not an OR
 * fallback) when no recognized prefix is present — so call sites can do
 * `(typesOp ?? subtypeOp) === 'OR_NOT'` and correctly fall through plain-string inputs.
 */
export function stripOperatorPrefix(raw: string | boolean | undefined | null): {
  operator: FilterOperator | undefined;
  value: string | boolean | undefined | null;
} {
  if (raw === undefined || raw === null) return { operator: undefined, value: raw };
  if (typeof raw === 'boolean') return { operator: undefined, value: raw };
  if (raw.includes(':')) {
    const [operator, values] = raw.split(':');
    if (VALID_OPERATORS.has(operator)) {
      return { operator: operator as FilterOperator, value: values };
    }
  }
  return { operator: undefined, value: raw };
}

// ---------------------------------------------------------------------------
// Write side: state → URL query string / POST body
// ---------------------------------------------------------------------------

/**
 * Prefix a value with `<op>:` when `shouldApply` is true. Used to build URL query strings
 * (e.g. `types=OR_NOT:INSTAGRAM_COMMENT`). Returns the raw value unchanged when the gate is
 * off, the value is nullish, or the value is an empty string.
 */
export function withOperator<T extends string | boolean | undefined | null>(
  value: T,
  operator: FilterOperator,
  shouldApply: boolean,
): T | string {
  if (value === undefined || value === null || value === '' || !shouldApply) {
    return value;
  }
  return `${operator}:${value}`;
}

/**
 * Wrap a value as `{ values, operator }` — matches the BE `tagIds`-style payload shape used
 * for POST bodies (e.g. the messages CSV endpoint). Empty arrays and nullish values pass
 * through so downstream `omitBy` can drop them.
 */
export function buildOperatorObject<V>(
  value: V,
  operator: FilterOperator,
  shouldApply: boolean,
): V | { values: V; operator: FilterOperator } {
  if (!shouldApply) return value;
  if (value === undefined || value === null) return value;
  if (Array.isArray(value) && value.length === 0) return value;
  return { values: value, operator };
}

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

/**
 * Filter a comma-separated string while keeping the leading `<op>:` prefix intact.
 * Without this, `value.split(',').filter(...).join(',')` glues the operator onto the first
 * element ("OR:FOO" instead of "FOO") which silently breaks predicate matching and drops
 * the operator from the rejoined output.
 *
 * Edge cases:
 *   - undefined / null / '' input → returned as-is (no work to do)
 *   - all values filtered out → returns '' (no prefix); callers should treat as "no selection"
 */
export function filterValuesPreservingOperator(
  value: string | undefined | null,
  predicate: (v: string) => boolean,
): string | undefined | null {
  if (!value) return value;
  const hadPrefix = typeof value === 'string' && value.includes(':');
  const { operator, values } = parseOperatorPrefix(value, undefined);
  const filtered = values
    ?.split(',')
    .filter((v) => predicate(v))
    .join(',');
  if (!filtered) return filtered;
  return hadPrefix ? `${operator}:${filtered}` : filtered;
}
